import * as React from "react";
import { CorrectionTypesEnum, DcfSystemWorkflow, DeviceTypes, StudyDcfSetting } from "../../misc/api/apiTypes";
import { RecordingStartItem } from "./CorrectionTypes/RecordingStart/RecordingStartItem";
import { SubjectIdItem } from "./CorrectionTypes/SubjectId/SubjectIdItem";
import { Correction, MiscCorrection, VisibleWizards } from "../Request/DcfRequestReducer";
import { DcfRequestAction, SetVisibleWizardAction } from "../Request/DcfRequestActions";
import { MiscCorrectionItem } from "./CorrectionTypes/Misc/MiscCorrectionItem";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { EntityLabelsItem } from "./CorrectionTypes/EntityLabels/EntityLabelsItem";
import { EntityLabelGroup } from "./CorrectionTypes/EntityLabels/EntityLabelCorrectionWizard";
import { SubjectTypeItem } from "./CorrectionTypes/SubjectType/SubjectTypeItem";
import { SuppressSessionItem } from "./CorrectionTypes/SuppressSession/SuppressSessionItem";
import { MediaSerialItem } from './CorrectionTypes/MediaSerial/MediaSerialItem';
import { SessionTypes } from '../../misc/api/ApiSessionTypes';
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { Subject, Visit, Session, CoughSession, WebPortalClient } from "webportalclient";
import { SwapLabelsItem } from "./CorrectionTypes/SwapLabels/SwapLabelsItem";
import { ConfigurableDataItemIds } from "../../misc/api/ApiConfigurableDataItems";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";

export interface CorrectionTypeSelectionProps {
    subject: Subject;

    miscCorrection: MiscCorrection;
    corrections: Correction[];
    recordId: string;
    allVisitsSelected: boolean;
    allSessionsSelected: boolean;
    session: Session;
    visit: Visit;

    dispatch: React.Dispatch<DcfRequestAction>;
}

const isCoughSession = (model: Session): model is CoughSession => {
    if (!model) return false;

    return model.sessionType === SessionTypes.Cough;
}

const GetSpecificSession = async (client: WebPortalClient, session: Session): Promise<Session> => {
    switch (session.sessionType) {
        case SessionTypes.Cough:
            var coughSession = await client.getCoughSession(session.id, `suppressed,mediaSerialNumber,name,id,occurred,recordingStart,sessionType,deviceType`);
            session.sessionScheduleItemId;
            coughSession['sessionScheduleItemId'] = session.sessionScheduleItemId;
            return coughSession;
        default:
            return session;
    }
};

export const CorrectionTypeSelection = ({
    recordId,
    subject,
    visit,
    session,
    corrections,
    miscCorrection,
    allVisitsSelected,
    allSessionsSelected,
    dispatch
}: CorrectionTypeSelectionProps): JSX.Element => {
    const [specificSession, setSpecificSession] = React.useState(session);
    const [dcfStudySettings, setDcfStudySettings] = React.useState<StudyDcfSetting>();
    const [isFetchingSpecificSession, setIsFetchingSpecificSession] = React.useState(false);
    const [showingMore, setShowingMore] = React.useState(false);
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const { dataItemConfigurationCollection, study } = useCurrentStudyConfigurationContext();

    React.useEffect(() => {
        const loadDcfCorrectionTypes = async () => {
                const dcfStudySettings = await legacyClient.getStudyDcfSettings(study.id);
                setDcfStudySettings(dcfStudySettings);
            };

        loadDcfCorrectionTypes();
    }, []);
    
    React.useEffect(
        () => {
            const fetchSpecificSession = async () => {
                setIsFetchingSpecificSession(true);
                const sessionEntity = await GetSpecificSession(client.client, session);

                setSpecificSession(sessionEntity);
                setIsFetchingSpecificSession(false);
            };

            if (session && session.id) {
                fetchSpecificSession();
            } else {
                setSpecificSession(null);
            }

            return (): void => setSpecificSession(null);
        },
        [session, visit, subject]
    );

    const isItemDisabled = (correctionName: VisibleWizards): boolean => {
        return (
            corrections.filter(x => x.sourceCorrection !== correctionName).length > 0 ||
            !!miscCorrection
        );
    };

    const isItemSelected = (correctionName: VisibleWizards): boolean => {
        return corrections.filter(x => x.sourceCorrection === correctionName).length > 0;
    };

    const subjectConfig = () => dataItemConfigurationCollection.find(dic => dic.configurableDataItemId == ConfigurableDataItemIds.SubjectId);

    const isDcfItemEnabled = (correctionType: string) => {
        return dcfStudySettings?.EnabledDcfTypes?.includes(correctionType) ?? false;
    };

    return (
        <>
            <div className="row">
                <div className="col-md-12">
                    <h3 className="text-primary">Requested Data Corrections</h3>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    {isFetchingSpecificSession ? (
                        <LoadingWithMessage message="Loading Possible Data Corrections..." />
                    ) : (
                            <ul className="posh-select">
                                {isCoughSession(specificSession) && (specificSession as CoughSession).recordingStart && isDcfItemEnabled(CorrectionTypesEnum.RecordingStart) ? (
                                <RecordingStartItem
                                        recordId={recordId}
                                    isDisabled={isItemDisabled(VisibleWizards.RecordingStart)}
                                    correctionSelected={isItemSelected(VisibleWizards.RecordingStart)}
                                    onClick={() =>
                                        dispatch(
                                            SetVisibleWizardAction.create(
                                                recordId,
                                                VisibleWizards.RecordingStart,
                                                (specificSession as CoughSession).recordingStart
                                            )
                                        )
                                    }
                                />
                            ) : null}
                                {!!subject &&
                                    (!subject.createdWithSpirotracV || dcfStudySettings.SystemWorkflow === DcfSystemWorkflow.Log) &&
                                    !visit &&
                                    !specificSession &&
                                    !allVisitsSelected &&
                                    !allSessionsSelected && isDcfItemEnabled(CorrectionTypesEnum.SubjectNumber) ? (
                                <SubjectIdItem
                                    subjectDisplayText={subjectConfig().displayText}
                                    recordId={recordId}
                                        isDisabled={isItemDisabled(VisibleWizards.SubjectNumber)}
                                        correctionSelected={isItemSelected(VisibleWizards.SubjectNumber)}
                                    onClick={() =>
                                        dispatch(
                                            SetVisibleWizardAction.create(
                                                recordId,
                                                VisibleWizards.SubjectNumber,
                                                subject.number
                                            )
                                        )
                                    }
                                />
                            ) : null}
                            {!!subject &&
                            (!subject.createdWithSpirotracV || dcfStudySettings.SystemWorkflow === DcfSystemWorkflow.Log) &&
                            !visit &&
                            !specificSession &&
                            !allVisitsSelected &&
                                    !allSessionsSelected && isDcfItemEnabled(CorrectionTypesEnum.SubjectType) ? (
                                <SubjectTypeItem
                                    recordId={recordId}
                                            isDisabled={isItemDisabled(VisibleWizards.SubjectType)}
                                            correctionSelected={isItemSelected(VisibleWizards.SubjectType)}
                                    onClick={() =>
                                        dispatch(
                                            SetVisibleWizardAction.create(
                                                recordId,
                                                VisibleWizards.SubjectType,
                                                subject.status
                                            )
                                        )
                                            } />
                                ) : null}
                                {!!subject && specificSession && isDcfItemEnabled(CorrectionTypesEnum.SessionSuppress) ? <SuppressSessionItem
                                    recordId={recordId}
                                    isDisabled={isItemDisabled(VisibleWizards.SessionSuppress)}
                                    correctionSelected={isItemSelected(VisibleWizards.SessionSuppress)}
                                    onClick={() => {
                                        dispatch(
                                            SetVisibleWizardAction.create(
                                                recordId,
                                                VisibleWizards.SessionSuppress,
                                                specificSession.suppressed.toString()
                                            )
                                        )
                                    }} />
                                    : null}
                                {!!subject &&
                                    (!subject.createdWithSpirotracV || dcfStudySettings.SystemWorkflow === DcfSystemWorkflow.Log) &&
                                    specificSession && isDcfItemEnabled(CorrectionTypesEnum.MoveSession) ? (
                                    <EntityLabelsItem
                                        recordId={recordId}
                                        isDisabled={isItemDisabled(VisibleWizards.EntityLabels)}
                                        correctionSelected={isItemSelected(VisibleWizards.EntityLabels)}
                                        onClick={() =>
                                            dispatch(
                                                SetVisibleWizardAction.create(
                                                    recordId,
                                                    VisibleWizards.EntityLabels,
                                                    {
                                                        subject: {
                                                            label: subject.number,
                                                            id: subject.id
                                                        },
                                                        visit: visit
                                                            ? {
                                                                label: visit.name,
                                                                id: visit.visitScheduleItemId
                                                            }
                                                            : undefined,
                                                        session: specificSession
                                                            ? {
                                                                label: specificSession.name,
                                                                id: specificSession.sessionScheduleItemId
                                                            }
                                                            : undefined
                                                    } as EntityLabelGroup
                                                )
                                            )
                                        }
                                    />
                                ) : null}
                                {!!subject && (!subject.createdWithSpirotracV || dcfStudySettings.SystemWorkflow === DcfSystemWorkflow.Log) && specificSession && isDcfItemEnabled(CorrectionTypesEnum.SwapLabels) ? (
                                    <SwapLabelsItem
                                        recordId={recordId}
                                        isDisabled={isItemDisabled(VisibleWizards.SwapLabels)}
                                        correctionSelected={isItemSelected(VisibleWizards.SwapLabels)}
                                        onClick={() =>
                                            dispatch(
                                                SetVisibleWizardAction.create(
                                                    recordId,
                                                    VisibleWizards.SwapLabels,
                                                    {
                                                        subject: {
                                                            number: subject.number,
                                                            id: subject.id
                                                        },
                                                        visit: visit
                                                            ? {
                                                                name: visit.name,
                                                                id: visit.id
                                                            }
                                                            : undefined,
                                                        session: specificSession
                                                            ? {
                                                                name: specificSession.name,
                                                                id: specificSession.id,
                                                                occurred: specificSession.occurred,
                                                                suppressed: specificSession.suppressed
                                                            }
                                                            : undefined
                                                    }
                                                )
                                            )
                                        }
                                    />
                                ) : null}
                                {!!subject && isCoughSession(specificSession) && specificSession.deviceType === DeviceTypes.VitaloJAK1 && isDcfItemEnabled(CorrectionTypesEnum.MediaSerial) ? <MediaSerialItem
                                    recordId={recordId}
                                    isDisabled={isItemDisabled(VisibleWizards.MediaSerial)}
                                    correctionSelected={isItemSelected(VisibleWizards.MediaSerial)}
                                    onClick={(): void =>
                                        dispatch(
                                            SetVisibleWizardAction.create(
                                                recordId,
                                                VisibleWizards.MediaSerial,
                                                (specificSession as CoughSession).mediaSerialNumber
                                            )
                                        )
                                    }
                                /> : null}
                                {isDcfItemEnabled(CorrectionTypesEnum.Miscellaneous) ? <><div className="row">
                                    <div className="col-md-12" style={{ marginBottom: '10px' }}>
                                        {showingMore ? <a className="btn btn-link" onClick={() => setShowingMore(false)}>Fewer options...</a> : <a onClick={() => setShowingMore(true)}>More options...</a>}
                                    </div>
                                </div>
                                <>
                                    {showingMore ? <MiscCorrectionItem
                                        recordId={recordId}
                                        isDisabled={corrections.length >= 1}
                                        correctionSelected={!!miscCorrection}
                                        onClick={(): void =>
                                            dispatch(SetVisibleWizardAction.create(recordId, VisibleWizards.Misc, null))
                                        }
                                    /> : null}
                                </>
                                </>
                                : null}
                            </ul>
                    )}
                </div>
            </div>
        </>
    );
};
